import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import { 
  Card, 
  Table, 
  Input, 
  Drawer, 
  Typography, 
  Row, 
  Col, 
  Statistic, 
  Form,
  Button,
  Upload,
  Tabs,
  Image,
  message,
  Modal
} from 'antd';
import { Bar } from 'react-chartjs-2';
import { PlusOutlined, EditOutlined, LoadingOutlined, UserAddOutlined } from '@ant-design/icons';
import { supabase } from '../../../api/supabase';

const { Title, Text } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;
const { TextArea } = Input;

const ClientManager = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('1');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .select(`
          *,
          services:services(
            *,
            transactions:transactions(*)
          )
        `);

      if (error) throw error;

      const processedClients = data.map(client => ({
        ...client,
        totalSpent: client.services?.reduce((sum, service) => 
          sum + (service.transactions?.reduce((serviceSum, transaction) => 
            serviceSum + (transaction.amount || 0), 0) || 0), 0) || 0,
        charterCount: client.services?.length || 0
      }));

      setClients(processedClients);
    } catch (error) {
      message.error('Error fetching clients');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Total Spent',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      render: (value) => `€${value.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}`,
      sorter: (a, b) => a.totalSpent - b.totalSpent,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Charters',
      dataIndex: 'charterCount',
      key: 'charterCount',
      sorter: (a, b) => a.charterCount - b.charterCount,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="primary" 
          icon={<EditOutlined />}
          onClick={() => handleClientSelect(record)}
        >
          View/Edit
        </Button>
      ),
    }
  ];

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    form.setFieldsValue({
      name: client.name,
      email: client.email,
      phone: client.phone,
      informations: client.informations
    });
    setDrawerVisible(true);
  };

  const handleCreateClient = async (values) => {
    try {
      const { error } = await supabase
        .from('clients')
        .insert([{
          ...values,
          created_at: new Date(),
          updated_at: new Date(),
          photos: []
        }]);

      if (error) throw error;

      message.success('Client created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchClients();
    } catch (error) {
      message.error('Error creating client');
      console.error(error);
    }
  };

  const handleUpdateClient = async (values) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          ...values,
          updated_at: new Date()
        })
        .eq('id', selectedClient.id);

      if (error) throw error;

      message.success('Client updated successfully');
      fetchClients();
    } catch (error) {
      message.error('Error updating client');
      console.error(error);
    }
  };

  const handlePhotoUpload = async ({ file }) => {
    setUploadingPhoto(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${selectedClient.id}/${Math.random()}.${fileExt}`;
      const filePath = `clients/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('client-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('client-photos')
        .getPublicUrl(filePath);

      const updatedPhotos = [...(selectedClient.photos || []), publicUrl];
      const { error: updateError } = await supabase
        .from('clients')
        .update({ 
          photos: updatedPhotos,
          updated_at: new Date()
        })
        .eq('id', selectedClient.id);

      if (updateError) throw updateError;

      fetchClients();
      message.success('Photo uploaded successfully');
    } catch (error) {
      message.error('Error uploading photo');
      console.error(error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async (photoUrl) => {
    try {
      const updatedPhotos = selectedClient.photos.filter(url => url !== photoUrl);
      const { error } = await supabase
        .from('clients')
        .update({ 
          photos: updatedPhotos,
          updated_at: new Date()
        })
        .eq('id', selectedClient.id);

      if (error) throw error;

      fetchClients();
      message.success('Photo deleted successfully');
    } catch (error) {
      message.error('Error deleting photo');
      console.error(error);
    }
  };

  const CreateClientForm = () => (
    <Modal
      title="Create New Client"
      open={createModalVisible}
      onCancel={() => setCreateModalVisible(false)}
      footer={null}
    >
      <Form
        form={createForm}
        layout="vertical"
        onFinish={handleCreateClient}
      >
        <Form.Item 
          name="name" 
          label="Name" 
          rules={[{ required: true, message: 'Please enter client name' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="email" label="Email">
          <Input type="email" />
        </Form.Item>

        <Form.Item name="phone" label="Phone">
          <Input />
        </Form.Item>

        <Form.Item name="informations" label="Informations">
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" block>
            Create Client
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }}>
    <Card className="w-full">
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2}>Client Management</Title>
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<UserAddOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            Add New Client
          </Button>
        </Col>
      </Row>
      
      <Search
        placeholder="Search by name, email, or phone..."
        allowClear
        enterButton
        size="large"
        onChange={(e) => {
          const value = e.target.value.toLowerCase();
          const filtered = clients.filter(client => 
            client.name?.toLowerCase().includes(value) ||
            client.email?.toLowerCase().includes(value) ||
            client.phone?.toLowerCase().includes(value)
          );
          setClients(filtered);
        }}
        style={{ marginBottom: 20 }}
      />

      <Table
        dataSource={clients}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <CreateClientForm />

      <Drawer
        title={selectedClient?.name}
        placement="right"
        width={1000}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Client Information" key="1">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleUpdateClient}
            >
              <Form.Item 
                name="name" 
                label="Name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>

              <Form.Item name="email" label="Email">
                <Input type="email" />
              </Form.Item>

              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>

              <Form.Item name="informations" label="Informations">
                <TextArea rows={4} />
              </Form.Item>

              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
            </Form>
          </TabPane>

          <TabPane tab="Photos" key="2">
            <Upload
              listType="picture-card"
              fileList={(selectedClient?.photos || []).map((url, index) => ({
                uid: `-${index}`,
                name: `photo-${index}`,
                status: 'done',
                url: url
              }))}
              customRequest={handlePhotoUpload}
              onRemove={(file) => handlePhotoDelete(file.url)}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('You can only upload image files!');
                }
                return isImage;
              }}
            >
              {uploadingPhoto ? <LoadingOutlined /> : <PlusOutlined />}
            </Upload>

            <div style={{ marginTop: 24 }}>
              <Image.PreviewGroup>
                {selectedClient?.photos?.map((url, index) => (
                  <Image
                    key={index}
                    width={200}
                    src={url}
                    style={{ marginRight: 8, marginBottom: 8 }}
                  />
                ))}
              </Image.PreviewGroup>
            </div>
          </TabPane>

          <TabPane tab="Services History" key="3">
            <Row gutter={16} className="mb-6">
              <Col span={8}>
                <Statistic 
                  title="Total Charters" 
                  value={selectedClient?.charterCount} 
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Total Spent" 
                  value={selectedClient?.totalSpent} 
                  prefix="€"
                  precision={2}
                />
              </Col>
            </Row>

            {selectedClient?.services?.map((service, index) => (
              <Card key={index} style={{ marginBottom: 16 }} size="small">
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <Text strong>Service Date:</Text> {new Date(service.service_date).toLocaleDateString('fr-FR')}
                  </Col>
                  <Col span={8}>
                    <Text strong>Service Type:</Text> {service.service_type}
                  </Col>
                  <Col span={8}>
                    <Text strong>Yacht:</Text> {service.yacht}
                  </Col>
                  <Col span={8}>
                    <Text strong>Departure:</Text> {service.departure}
                  </Col>
                  <Col span={8}>
                    <Text strong>Return:</Text> {service.return}
                  </Col>
                  <Col span={8}>
                    <Text strong>Passengers:</Text> {service.pax}
                  </Col>
                  <Col span={8}>
                    <Text strong>Children:</Text> {service.children}
                  </Col>
                  <Col span={8}>
                    <Text strong>Status:</Text> {service.service_status}
                  </Col>
                  <Col span={24}>
                    <Text strong>Notes:</Text> {service.notes}
                  </Col>
                  <Col span={24}>
                    <Text strong>Transactions:</Text>
                    {service.transactions?.map((transaction, index) => (
                      <div key={index} style={{ marginTop: 8 }}>
                        <Text>Date: {new Date(transaction.transaction_date).toLocaleDateString('fr-FR')}</Text>
                        <Text style={{ marginLeft: 16 }}>Amount: €{transaction.amount.toFixed(2)}</Text>
                        <Text style={{ marginLeft: 16 }}>Status: {transaction.status}</Text>
                      </div>
                    ))}
                  </Col>
                </Row>
              </Card>
            ))}
          </TabPane>
        </Tabs>
      </Drawer>
    </Card>
    </div>
  );
};

export default ClientManager;